import React, { createContext, useContext, ReactNode, useState, useEffect, useMemo, useCallback } from 'react';
import { businessAuthAPI } from '../services/api';
import { Business, BusinessAuthResponse, TokenVerificationResponse } from '../types/models';
import i18n from '../i18n/config';

interface AuthContextType {
  business: Business | null;
  token: string | null;
  refreshToken: string | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<Business>;
  register: (data: any) => Promise<void>;
  logout: () => void;
  refreshBusinessProfile: () => Promise<void>;
  updateBusinessProfile: (data: any) => Promise<void>;
  verifyToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [business, setBusiness] = useState<Business | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('businessToken'));
  const [refreshToken, setRefreshToken] = useState<string | null>(localStorage.getItem('businessRefreshToken'));
  const [loading, setLoading] = useState(true);

  const isAuthenticated = useMemo(() => !!token && !!business, [token, business]);

  // Helper function to apply user's language preference
  const applyLanguagePreference = (businessData: Business | null) => {
    if (businessData?.preferences?.lang) {
      const userLang = businessData.preferences.lang;
      if (userLang === 'tr' || userLang === 'en') {
        i18n.changeLanguage(userLang);
        // Update document language
        document.documentElement.lang = userLang;
        // Update localStorage for i18n
        localStorage.setItem('i18nextLng', userLang);
      }
    }
  };

  // Initialize auth state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('businessToken');
      if (storedToken) {
        try {
          const response = await businessAuthAPI.verifyToken(storedToken);
          if (response.data.valid) {
            const businessData = response.data.business as Business;
            setBusiness(businessData);
            setToken(storedToken);
            // Apply user's language preference
            applyLanguagePreference(businessData);
          } else {
            // Token invalid, clear storage
            localStorage.removeItem('businessToken');
            localStorage.removeItem('businessRefreshToken');
            setToken(null);
            setRefreshToken(null);
          }
        } catch (error) {
          console.error('Token verification failed:', error);
          localStorage.removeItem('businessToken');
          localStorage.removeItem('businessRefreshToken');
          setToken(null);
          setRefreshToken(null);
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<Business> => {
    try {
      setLoading(true);
      const response = await businessAuthAPI.login({ email, password });
      const data: BusinessAuthResponse = response.data;
      
      // Store tokens
      localStorage.setItem('businessToken', data.token);
      localStorage.setItem('businessRefreshToken', data.refreshToken);
      
      setToken(data.token);
      setRefreshToken(data.refreshToken);
      const businessData = data.business as Business;
      setBusiness(businessData);
      // Apply user's language preference
      applyLanguagePreference(businessData);
      return businessData;
    } catch (error: any) {
      console.error('Login failed:', error);
      throw new Error(error.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (data: any) => {
    try {
      setLoading(true);
      const response = await businessAuthAPI.register(data);
      const responseData: BusinessAuthResponse = response.data;
      
      // Store tokens
      localStorage.setItem('businessToken', responseData.token);
      localStorage.setItem('businessRefreshToken', responseData.refreshToken);
      
      setToken(responseData.token);
      setRefreshToken(responseData.refreshToken);
      const businessData = responseData.business as Business;
      setBusiness(businessData);
      // Apply user's language preference
      applyLanguagePreference(businessData);
    } catch (error: any) {
      console.error('Registration failed:', error);
      throw new Error(error.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('businessToken');
    localStorage.removeItem('businessRefreshToken');
    setToken(null);
    setRefreshToken(null);
    setBusiness(null);
  }, []);

  const refreshBusinessProfile = useCallback(async () => {
    try {
      console.log('🔄 AuthContext: refreshBusinessProfile called');
      const response = await businessAuthAPI.getProfile();
      console.log('📦 AuthContext: Profile response received');
      console.log('📦 AuthContext: Full response data:', JSON.stringify(response.data, null, 2));
      const businessData = response.data as Business;
      console.log('📦 AuthContext: Parsed business data:', {
        _id: businessData._id,
        name: businessData.name,
        email: businessData.email,
        phone: businessData.phone,
        category: businessData.category,
        website: businessData.website,
        description: businessData.description,
        location: businessData.location,
        preferences: businessData.preferences,
        facebook: businessData.facebook,
        instagram: businessData.instagram,
        twitter: businessData.twitter,
        tiktok: businessData.tiktok,
        linkedin: businessData.linkedin,
        analysisStatus: businessData.analysisStatus,
        isActive: businessData.isActive,
        emailVerified: businessData.emailVerified,
        createdAt: businessData.createdAt,
        updatedAt: businessData.updatedAt
      });
      
      // Only update if business ID or updatedAt changed to prevent unnecessary re-renders
      setBusiness(prevBusiness => {
        if (prevBusiness?._id === businessData._id && prevBusiness?.updatedAt === businessData.updatedAt) {
          console.log('ℹ️ AuthContext: Business data unchanged, skipping update');
          return prevBusiness; // Return same reference to prevent re-render
        }
        console.log('💾 AuthContext: Business state updated with new data');
        return businessData;
      });
      
      // Apply user's language preference
      applyLanguagePreference(businessData);
    } catch (error) {
      console.error('❌ AuthContext: Failed to refresh business profile:', error);
      throw error;
    }
  }, []); // No dependencies - function is stable

  const updateBusinessProfile = useCallback(async (data: any) => {
    try {
      console.log('🔄 AuthContext: updateBusinessProfile called with data:', data);
      const response = await businessAuthAPI.updateProfile(data);
      console.log('📦 AuthContext: Update response received', response.data);
      const businessData = response.data as Business;
      
      // Always update on save (user made changes)
      setBusiness(businessData);
      console.log('💾 AuthContext: Business state updated after save', businessData);
      
      // Apply user's language preference if lang was updated
      if (data.preferences?.lang) {
        applyLanguagePreference(businessData);
      }
    } catch (error) {
      console.error('❌ AuthContext: Failed to update business profile:', error);
      throw error;
    }
  }, []); // No dependencies - function is stable

  const verifyToken = useCallback(async (): Promise<boolean> => {
    if (!token) return false;
    
    try {
      const response = await businessAuthAPI.verifyToken(token);
      return response.data.valid;
    } catch (error) {
      console.error('Token verification failed:', error);
      return false;
    }
  }, [token]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo<AuthContextType>(() => ({
    business,
    token,
    refreshToken,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    refreshBusinessProfile,
    updateBusinessProfile,
    verifyToken
  }), [business, token, refreshToken, loading, isAuthenticated, login, register, logout, refreshBusinessProfile, updateBusinessProfile, verifyToken]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};